import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components (reusable components like SubcodonProfile)
import GeneralSettings from "../../Components/Common/GeneralSettings";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ActionButton from "../../Components/Common/ActionButton";
import TranscriptChooserModal from "../../Components/Common/TranscriptChooserModal";

// Page-specific Components
import GroupsManager from "./components/GroupsManager";

// API
import {
  getComparisonPlotData,
  generateComparisonPlot,
} from "../../helpers/eirna_backend_helper";

// Configuration (config-driven like other modern components)
import {
  comparisonProfileConfig,
  comparisonProfileDefaults,
  comparisonProfileValidation,
} from "../../config/plotConfigs/comparisonProfileConfig";

// Hooks (reusable hooks like SubcodonProfile)
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useGroupsManager } from "./hooks/useGroupsManager";
import { EnterButtonListener} from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useComparisonProfileShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

const ComparisonProfile = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [tableHeaders, setTableHeaders] = useState(null); 
  const [tableRows, setTableRows] = useState([]);

  // Reference points to the plot container, to allow it to scroll on plot completion
  const plotContainerRef  = useRef(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
  });

  // Enhanced hooks with reusable components (like SubcodonProfile)
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(comparisonProfileDefaults, comparisonProfileValidation);

  const {
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
  } = useFileSelection("riboseq");

  const {
    groups,
    addGroup,
    setGroupsFromPayload,
    removeGroup,
    updateGroupName,
    updateGroupColor,
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    autoFillGroups,
    autoLabelGroups,
    clearAllGroups,
    validateGroupsForComparison,
    getMasterFileDict,
  } = useGroupsManager();

  // Use the Comparison Profile specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useComparisonProfileShortCode({
      location,
      updateMultipleSettings,
      clearAllGroups,
      addFilesToGroup,
      updateGroupName,
      updateGroupColor,
    });

  const shortCodeRef = useRef(userPassedShortCode);
  useEffect(() => {
    shortCodeRef.current = userPassedShortCode;
  }, [userPassedShortCode]);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getComparisonPlotData(projectId);
        setApiData(response);

        if (response.default_tran && !shortCodeRef.current) {
          updateSetting("transcript", response.default_tran);
        }

        
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];

        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        // Set any values passed as url parameters
        const params = new URLSearchParams(location.search);
        // An array of id's of any non int params (text input or radio) that can be passed
        const textURLParams = ["transcript", 
                               "highlightStart",
                               "highlightStop",
                               "normalizationType"];

        for (let textURLParam of textURLParams) {
          const paramVal = params.get(textURLParam);
          if (paramVal) {
            updateSetting(textURLParam, paramVal);
          };
        }

        // An array of id's of integer parameters that can be passed
        const intURLParams = ["minRead",
                              "maxRead",
                              "max_diff_pos"];

        for (let intURLParam of intURLParams) {
          const paramVal = params.get(intURLParam);
          if (paramVal) {
            // Special case for if max_diff_pos is passed, turn cumsum_check to true
            if (intURLParam == "max_diff_pos") {
              updateSetting("cumsum_check", true);
            }
            updateSetting(intURLParam, Number(paramVal));
          };
        }

        // Will be set to true if any group info is passed in url parameters
        var groupParamsPassed = false;

        // parse url parameters for each group from 1 to 32, for each group will check if
        // a file list and a label has been passed and attempt to populate on the page
        for (let i = 1; i <= 32; i++) {
          // Get the raw url param files value for this group 
          const filesVal = params.get(`grp${i}_files`);
          if (filesVal) {
              // Set groupParamsPassed to true so plot will auto generate after parsing
              // all group url params
              groupParamsPassed = true;
              // Convert from strings to ints
              const ids = filesVal
                .split(',')
                .map(s => Number(s.trim()))
                .filter(Number.isFinite); 

              // If there are file ids in the array update the corresponding group
              if (ids.length) { 
                addFilesToGroup(i, ids);
              }
          };

          // Get the raw url param label value for this group 
          const labelVal = params.get(`grp${i}_label`);
          if (labelVal) {
            updateGroupName(i, labelVal);
          }

        }
        // If any group info was passed in params, auto generate plot
        if (groupParamsPassed) {
          setAutoRunPlot(true);
        }



      } catch (err) {
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, updateSetting, setSelectedSeqType]);

  // Auto-trigger plot generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        groupsCount: groups.length,
        hasFilesInGroups: groups.some((g) => g.files.length > 0),
      });

      const hasFilesForComparison =
        groups.length >= 2 && groups.every((g) => g.files.length > 0);

      if (!plotLoading && !loading && hasFilesForComparison) {
        console.log(
          "All conditions met, generating comparison plot from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGeneratePlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    groups.map((g) => g.files.length).join(","),
    setAutoRunPlot,
  ]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Handle generate plot
  const handleGeneratePlot = useCallback(async () => {
    // Validate general settings
    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Validate groups for comparison
    const groupValidation = validateGroupsForComparison();
    if (!groupValidation.isValid) {
      toast.error(groupValidation.errors[0]);
      return;
    }

    // Additional validation
    if (!settings.transcript?.trim()) {
      toast.error("Please enter a gene/transcript");
      return;
    }

    try {
      setPlotLoading(true);
      setError(null);

      // Construct master_file_dict from groups
      const masterFileDict = getMasterFileDict();

      // Match the comparison API schema exactly
      // Use the Comparison Profile specific payload creator
      const plotRequest = createPlotSpecificPayload.comparisonProfile({
        settings,
        projectId,
        userPassedShortCode,
        masterFileDict,
      });
      console.log("Comparison plot request:", plotRequest);
      const response = await generateComparisonPlot(plotRequest);

      if (response.header_row) {
        // Show transcript selection table
        setTableHeaders(response.header_row);
        setTableRows(response.result_rows || []);
        setChooserOpen(true);     // open centered popup
        setPlotData(null);
        setError(null);
        toast.info("Select a transcript");
      } else if (response.message) {
        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(response.message);
        toast.error(response.message);
      } else if (response.plot) {
        setPlotData(response.plot);
        toast.success("Comparison plot generated successfully");
      }
    } catch (err) {
      let errorMsg = "Failed to generate comparison plot";

      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      console.error("Comparison plot error:", err);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPlotLoading(false);
    }
  }, [
    settings,
    validateSettings,
    validateGroupsForComparison,
    getMasterFileDict,
    projectId,
    navigate,
  ]);

  // When a user picks a transcript from the transcript table, update the transcript 
  // input box with chosen transcript, close the pop-up and generate the plot
  const handleSelectTranscript = useCallback((selectedTran) => {
    updateSetting("transcript", selectedTran);
    setChooserOpen(false);
    // set AutoRunPlot to true to trigger plot creation
    setAutoRunPlot(true);
  }, [updateSetting, handleGeneratePlot]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Comparison Profile | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Comparison Plot" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Comparison Plot
                </h4>

              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={updateSetting}
                onReset={resetSettings}
                config={comparisonProfileConfig}
                title="General Settings"
                showReset={true}
                loading={loading}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <GroupsManager
                groups={groups}
                allFiles={apiData.json_files}
                filteredFiles={filteredFiles}
                onAddGroup={addGroup}
                onRemoveGroup={removeGroup}
                onUpdateGroupName={updateGroupName}
                onUpdateGroupColor={updateGroupColor}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onClearGroup={clearGroup}
                onAutoFill={autoFillGroups}
                onAutoLabel={autoLabelGroups}
                onClearAll={clearAllGroups}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={12} className="text-end">
              <ActionButton
                onClick={handleGeneratePlot}
                loading={plotLoading}
                buttonType="plot"
              />
            </Col>
          </Row>

          <TranscriptChooserModal
            isOpen={chooserOpen}
            toggle={() => setChooserOpen(false)}
            headers={tableHeaders || []}
            rows={tableRows}
            onSelect={handleSelectTranscript}
            loading={plotLoading}
          />

          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={plotData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={groups.reduce(
                    (sum, g) => sum + g.files.length,
                    0
                  )}
                  groupsCount={groups.filter((g) => g.files.length > 0).length}
                />
              </Col>
            </Row>
          )}
          <ToastContainer limit={1} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ComparisonProfile;
